import { cn } from '@tempo-io/sui-class-utils';
import { Warning } from '@tempo-io/sui-icons';
import { Separator } from '@tempo-io/sui-separator';
import { parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { SchedulingValidation } from '@/lib/api/types/adaptive-planner/scenario-item';
import { formatLocalDate } from '@/lib/utils/common/dateFormats';
import { getSchedulingValidationMessage } from './getSchedulingValidationMessage';
import { TooltipScheduleBar } from './TooltipScheduleBar';

interface TooltipDatesSectionProps {
  expectedStart?: string;
  expectedFinish?: string;
  latestFinish?: string;
  isAtRisk: boolean;
  name: string;
  schedulingValidations?: SchedulingValidation[] | null;
}

export const TooltipDatesSection = ({
  expectedStart,
  expectedFinish,
  latestFinish,
  isAtRisk,
  name,
  schedulingValidations,
}: TooltipDatesSectionProps) => {
  const { t } = useTranslation();
  const isEmpty = isAtRisk && !expectedStart && !expectedFinish && !latestFinish;

  return (
    <div className="space-y-2">
      {expectedStart && expectedFinish && (
        <TooltipScheduleBar
          expectedStart={expectedStart}
          expectedFinish={expectedFinish}
          latestFinish={latestFinish}
          isAtRisk={isAtRisk}
        />
      )}
      <div className="flex justify-between">
        {expectedStart && (
          <div className="flex flex-col">
            <span className="text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.expectedStart)}:
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(expectedStart).toISOString())}
            </span>
          </div>
        )}

        {expectedFinish && (
          <div className="flex flex-col items-end">
            <span className="text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.expectedFinish)}:
            </span>
            <span className="text-xs text-text-neutral-secondary">
              {formatLocalDate(parseISO(expectedFinish).toISOString())}
            </span>
          </div>
        )}
      </div>
      {latestFinish && (
        <div className="flex w-full justify-end">
          <div className="flex flex-col items-end">
            <span className="text-xs font-medium">
              {t(($) => $.adaptivePlanner.scenarioDetails.latestFinish)}:
            </span>
            <span className={cn('text-xs', isAtRisk && 'text-text-error')}>
              {formatLocalDate(parseISO(latestFinish).toISOString())}
            </span>
          </div>
        </div>
      )}
      {isAtRisk && latestFinish && <Separator />}
      {isAtRisk && (
        <div className="flex items-start gap-3">
          <Warning className="h-4 w-4 text-text-error shrink-0 pt-1" />
          {isEmpty && schedulingValidations != null && schedulingValidations.length > 0 ? (
            <ul className="text-sm text-text-error space-y-1 list-none">
              {schedulingValidations.map((validation) => (
                <li className="text-wrap" key={validation}>
                  {getSchedulingValidationMessage(validation, t)}
                </li>
              ))}
            </ul>
          ) : (
            <span className="text-sm text-text-error">
              {isEmpty
                ? t(($) => $.adaptivePlanner.scenarioDetails.table.scheduleBar.tooltip.empty)
                : t(($) => $.adaptivePlanner.scenarioDetails.table.scheduleBar.tooltip.warning, {
                    name,
                  })}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
